import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ViewType, CheckValue } from '../data/homepageData';
import { COMPARISON_DATA } from '../data/homepageData';

interface ComparisonTableProps {
  /** Accepted for prop consistency — table content is identical for both views */
  view: ViewType;
}

/** Renders a check/dash/tilde based on the CheckValue */
function CheckIndicator({ value }: { value: CheckValue }) {
  if (value === true)
    return <span className="cv-check-yes font-bold" style={{ color: 'var(--cv-green)' }}>✓</span>;
  if (value === false)
    return <span className="cv-check-no" style={{ color: 'var(--cv-text-faint)' }}>—</span>;
  return <span className="cv-check-partial" style={{ color: 'var(--cv-orange)' }}>~</span>;
}

/**
 * Feature comparison table: CreatorVault vs Traditional Agency vs Other Platforms.
 * The highlighted CreatorVault column has a subtle purple tint.
 */
const ComparisonTable: React.FC<ComparisonTableProps> = ({ view: _view }) => {
  return (
    <section
      className="relative"
      style={{ padding: 'clamp(64px, 10vw, 140px) 0' }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 mb-4"
            style={{
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--cv-accent)',
            }}
          >
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
            COMPARISON
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-extrabold tracking-tight mb-4"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-2xl)',
              letterSpacing: '-0.02em',
            }}
          >
            Why <span className="cv-text-gradient">CreatorVault</span>?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto"
            style={{
              fontSize: 'var(--text-base)',
              color: 'var(--cv-text-muted)',
              maxWidth: '560px',
              lineHeight: 1.7,
            }}
          >
            See how we compare to traditional agencies and other platforms.
          </motion.p>
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="cv-glass mt-14 overflow-hidden"
          style={{ padding: 0, overflowX: 'auto' }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--text-sm)',
            }}
          >
            <thead>
              <tr>
                {/* Feature column header */}
                <th
                  className="text-left font-bold whitespace-nowrap"
                  style={{
                    padding: '16px 20px',
                    fontFamily: 'var(--font-display)',
                    fontSize: '14px',
                    color: 'var(--cv-text)',
                    borderBottom: '1px solid var(--cv-border)',
                  }}
                >
                  Feature
                </th>

                {/* CreatorVault — highlighted */}
                <th
                  className="text-left whitespace-nowrap"
                  style={{
                    padding: '16px 20px',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: 'var(--cv-accent)',
                    borderBottom: '1px solid var(--cv-border)',
                  }}
                >
                  CreatorVault
                </th>

                {/* Traditional Agency */}
                <th
                  className="text-left whitespace-nowrap"
                  style={{
                    padding: '16px 20px',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: 'var(--cv-text-muted)',
                    borderBottom: '1px solid var(--cv-border)',
                  }}
                >
                  Traditional Agency
                </th>

                {/* Other Platforms */}
                <th
                  className="text-left whitespace-nowrap"
                  style={{
                    padding: '16px 20px',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: 'var(--cv-text-muted)',
                    borderBottom: '1px solid var(--cv-border)',
                  }}
                >
                  Other Platforms
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_DATA.map((row, i) => (
                <tr key={i}>
                  {/* Feature label */}
                  <td
                    className="font-medium"
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid var(--cv-border)',
                      color: 'var(--cv-text)',
                      fontSize: '14px',
                    }}
                  >
                    {row.feature}
                  </td>

                  {/* CreatorVault — highlighted column */}
                  <td
                    className="text-center"
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid var(--cv-border)',
                      background: 'rgba(168, 85, 247, 0.04)',
                      fontSize: '14px',
                    }}
                  >
                    <CheckIndicator value={row.cv} />
                  </td>

                  {/* Traditional Agency */}
                  <td
                    className="text-center"
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid var(--cv-border)',
                      color: 'var(--cv-text-muted)',
                      fontSize: '14px',
                    }}
                  >
                    <CheckIndicator value={row.traditional} />
                  </td>

                  {/* Other Platforms */}
                  <td
                    className="text-center"
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid var(--cv-border)',
                      color: 'var(--cv-text-muted)',
                      fontSize: '14px',
                    }}
                  >
                    <CheckIndicator value={row.other} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonTable;
